import { EmptyState } from "@/components/widgets";
import { Badge } from "@/components/ui/badge";
import { ResponsiveContainer, AreaChart, Area } from "recharts";
import { ArrowDown, ArrowUp, Factory } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tile } from "./primitives";
import { BUCKET_COLORS, GOLD, NAVY, SPARK_MARGIN } from "./constants";

export const KpiTiles = ({ k, pl, pStartISO, navigate }) => (
  <>
    <Tile
      label="Open Complaints" value={k.open_now.toLocaleString()}
      sub="Accountable · OEM delays excluded"
      onClick={() => navigate("/complaints?open=1")}
      testId="kpi-open-tile" className="order-1 lg:order-none lg:col-span-2"
    />
    <Tile
      label="Red (5+ Days)" value={k.red_count.toLocaleString()} variant="crimson"
      sub="Click for oldest-first list"
      onClick={() => navigate("/complaints?rag=Red&open=1")}
      testId="kpi-red-tile" className="order-2 lg:order-none lg:col-span-2"
    />
    <Tile
      label={`New · ${pl}`} value={k.opened_period.toLocaleString()}
      delta={k.opened_delta} deltaGoodWhenDown
      sub={`vs ${k.opened_prev_same_point} by ${k.prev_same_point}`}
      onClick={() => navigate(`/complaints?date_from=${pStartISO}`)}
      testId="kpi-new-tile" className="order-6 lg:order-none lg:col-span-2"
    />
    <Tile
      label={`Closed · ${pl}`} value={k.closed_period.toLocaleString()}
      delta={k.closed_delta} deltaGoodWhenDown={false}
      warn={k.inflow_warning} warnIcon={k.inflow_warning}
      sub={
        <span className="flex items-center gap-1 flex-wrap">
          <span className="tabular">closure rate {k.closure_rate}%</span>
          {k.inflow_warning && <span className="text-[hsl(var(--status-amber))]">· inflow exceeds outflow</span>}
        </span>
      }
      onClick={() => navigate(`/complaints?status=Resolved,Closed&closed_from=${pStartISO}`)}
      testId="kpi-closed-tile" className="order-7 lg:order-none lg:col-span-2"
    />
    <Tile
      label="Avg Resolution Days" value={k.avg_res_period ?? "—"}
      sub={`Prev ${pl.toLowerCase()}: ${k.avg_res_prev ?? "—"}`}
      onClick={() => navigate(`/complaints?status=Resolved,Closed&closed_from=${pStartISO}`)}
      testId="kpi-avgres-tile" className="order-8 lg:order-none lg:col-span-2"
    />
    <Tile
      label={`Reopened · ${pl}`} value={k.reopened_period}
      warn={k.reopen_warning} warnIcon={k.reopen_warning}
      sub={k.reopen_warning ? `${k.reopen_pct}% of closures — check fake closures` : `${k.reopen_pct}% of ${k.closed_period} closures`}
      onClick={() => navigate("/complaints?reopened=1")}
      testId="kpi-reopened-tile" className="order-9 lg:order-none lg:col-span-2"
    />
  </>
);

export const OemStrip = ({ oem, navigate }) => {
  if (!oem) return null;
  return (
    <div
      className="col-span-2 lg:col-span-12 order-3 lg:order-none rounded-[var(--radius)] bg-[hsl(var(--cx-navy))] text-white px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2"
      data-testid="oem-delay-strip"
    >
      <span className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] font-medium text-white/80">
        <Factory size={15} className="text-[hsl(var(--cx-gold))]" /> OEM Delivery Delays — not counted in your scores
      </span>
      <span className="text-sm tabular" data-testid="oem-strip-open"><strong className="font-serif text-lg">{oem.open}</strong> open</span>
      <span className="text-sm tabular"><strong className="font-serif text-lg">{oem.avg_days_pending}</strong> avg days pending</span>
      <span className="text-sm tabular"><strong className="font-serif text-lg">{oem.oldest_days}</strong>d oldest</span>
      <span className="text-sm tabular">{oem.new_this_month} new · {oem.closed_this_month} resolved this month</span>
      <button
        className="ml-auto text-xs underline underline-offset-2 text-[hsl(var(--cx-gold))] hover:text-white transition-colors"
        onClick={() => navigate("/oem-delays")}
        data-testid="oem-strip-view-report"
      >
        View OEM report →
      </button>
    </div>
  );
};

/* ---------- Aging funnel ---------- */
export const AgingFunnel = ({ funnel, navigate }) => {
  const total = funnel.reduce((s, b) => s + b.count, 0) || 1;
  return (
    <div className="px-5 pb-4 space-y-2.5">
      {funnel.map((b) => (
        <button
          key={b.bucket}
          className="w-full text-left group"
          data-testid={`aging-bucket-${b.bucket.replace("+", "plus")}`}
          onClick={() => {
            const p = new URLSearchParams({ open: "1", min_days: String(b.min_days) });
            if (b.max_days !== null && b.max_days !== undefined) p.set("max_days", String(b.max_days));
            navigate(`/complaints?${p.toString()}`);
          }}
        >
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-medium group-hover:underline underline-offset-2">{b.bucket} days</span>
            <span className="tabular font-semibold" style={{ color: BUCKET_COLORS[b.color] }}>{b.count.toLocaleString()}</span>
          </div>
          <div className="h-4 rounded bg-[hsl(var(--cx-surface-2))] overflow-hidden border">
            <div
              className="h-full transition-all group-hover:opacity-80"
              style={{ width: `${Math.max((b.count / total) * 100, b.count ? 1.5 : 0)}%`, background: BUCKET_COLORS[b.color] }}
            />
          </div>
        </button>
      ))}
    </div>
  );
};

/* ---------- Function split ---------- */
export const FnSplit = ({ split, navigate, setFnFilter }) => (
  <div className="grid grid-cols-2 gap-3 px-5 pb-4">
    {["Sales", "Service"].map((fn) => {
      const d = split[fn];
      return (
        <button
          key={fn}
          className="border rounded-lg p-3 text-left hover:bg-[hsl(var(--cx-surface))] transition-colors"
          data-testid={`fn-split-${fn.toLowerCase()}`}
          onClick={() => {
            setFnFilter(fn);
            navigate("/complaints?open=1");
          }}
        >
          <p className="text-[0.62rem] uppercase tracking-[0.12em] text-muted-foreground font-medium">{fn}</p>
          <div className="flex items-baseline gap-3 mt-1">
            <span className="kpi-value text-xl">{d.open.toLocaleString()}</span>
            <span className="text-xs text-[hsl(var(--status-red))] font-semibold tabular">{d.red} red</span>
          </div>
          <div className="h-9 mt-1 -mx-1">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.spark} margin={SPARK_MARGIN}>
                <Area type="monotone" dataKey="count" stroke={fn === "Sales" ? GOLD : NAVY}
                  fill={fn === "Sales" ? GOLD : NAVY} fillOpacity={0.12} strokeWidth={1.5} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </button>
      );
    })}
  </div>
);

/* ---------- Top categories ---------- */
export const TopCategories = ({ cats, navigate }) => (
  <div className="px-2 pb-3">
    {cats.items.length === 0 ? (
      <EmptyState title="No complaints this month" />
    ) : (
      cats.items.map((c, i) => (
        <button
          key={c.category}
          className={cn(
            "w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-left hover:bg-[hsl(var(--cx-surface))] transition-colors",
            c.rising && "bg-[hsl(var(--accent))] border border-[hsl(var(--cx-gold)/0.45)]"
          )}
          data-testid={`top-category-row-${i}`}
          onClick={() => {
            const p = new URLSearchParams({ category: c.category, date_from: `${cats.period}-01` });
            navigate(`/complaints?${p.toString()}`);
          }}
        >
          <span className="flex items-center gap-2 min-w-0">
            <span className="text-xs text-muted-foreground tabular w-4">{i + 1}</span>
            <span className="text-sm truncate">{c.category}</span>
            {c.rising && <Badge variant="outline" className="text-[0.6rem] border-[hsl(var(--cx-gold))] text-[hsl(var(--cx-gold))]">+{c.change_pct}%</Badge>}
          </span>
          <span className="flex items-center gap-2 shrink-0">
            <span className="tabular text-sm font-semibold">{c.count}</span>
            {c.direction === "up" && <ArrowUp size={13} className="text-[hsl(var(--status-red))]" />}
            {c.direction === "down" && <ArrowDown size={13} className="text-[hsl(var(--status-green))]" />}
          </span>
        </button>
      ))
    )}
  </div>
);
